import { publicUrl } from "./newsletter-html";
import { SECTION_LABEL } from "./sections";
import type { IssueDetail } from "./segment-types";
import { utmCampaignFromTitle, utmMissing } from "./utm";

export type PreflightTone = "ok" | "warn" | "fail";

export interface PreflightCheck {
  id: string;
  ok: boolean;
  tone: PreflightTone;
  label: string;
  detail?: string;
  /** What to do about it, phrased as the action — shown when the check fails. */
  fix?: string;
  /** The part of the editor that fixes it, for the jump link. */
  anchor?: "audience" | "details" | "content" | "sending";
}

export interface PreflightResult {
  checks: PreflightCheck[];
  blocking: boolean;
  warnings: number;
}

const TOKEN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
const ALLOWED_VARS = new Set(["first_name", "name"]);

export function runPreflight(issue: Pick<IssueDetail, "title" | "subject" | "preheader" | "blocks" | "sections" | "utm" | "sender_name" | "reply_to" | "categories">): PreflightResult {
  const blocks = issue.blocks ?? [];
  const sections = issue.sections ?? [];
  const sectionArticles = sections.flatMap((s) => s.articles ?? []);

  const blob = [
    issue.title,
    issue.subject,
    issue.preheader,
    ...blocks.flatMap((b) => [b.text, b.alt, b.url]),
    ...sections.flatMap((s) => [s.heading, s.text, s.url, s.cta_label]),
    ...sectionArticles.flatMap((a) => [a.title, a.excerpt, a.url]),
  ].join("\n");

  const links = [
    ...blocks.map((b) => b.url),
    ...sections.map((s) => s.url),
    ...sectionArticles.map((a) => a.url),
  ].filter((u): u is string => !!u?.trim());
  const broken = links.filter((u) => !publicUrl(u) && !u.includes("{{"));

  // Article thumbnails count as images: a broken one is as visible to a
  // recipient as a broken hero.
  const imageUrls = [
    ...blocks.filter((b) => b.type === "image" || b.type === "hero").map((b) => b.url),
    ...sections.filter((s) => s.type === "image" || s.type === "banner").map((s) => s.image),
    ...sectionArticles.map((a) => a.image),
  ].filter((u): u is string => !!u?.trim());
  const images = imageUrls;
  const missingImages = imageUrls.filter((u) => !publicUrl(u));
  const tokens = [...blob.matchAll(TOKEN)].map((m) => m[1]);
  const emptyVars = tokens.filter((t) => !ALLOWED_VARS.has(t));
  const hasUnsub = blocks.some((b) => b.type === "footer")
    || sections.some((s) => s.type === "footer")
    || /unsub|إلغاء الاشتراك|unsubscribe/i.test(blob);
  const utmGaps = utmMissing({
    source: issue.utm?.source ?? "altibbi-newsletter",
    medium: issue.utm?.medium ?? "email",
    campaign: issue.utm?.campaign || utmCampaignFromTitle(issue.title),
    content: issue.utm?.content,
  });
  const prose = [
    ...blocks.filter((b) => b.type === "text").map((b) => b.text),
    ...sections.filter((s) => s.type === "text" || s.type === "tip").map((s) => s.text),
    ...sectionArticles.map((a) => a.excerpt),
  ].filter((t) => (t || "").trim().length > 40);
  const imageHeavy = images.length > 0 && prose.length === 0;
  const linkCount = blocks.filter((b) => (b.type === "link" || b.type === "cta" || b.type === "article") && publicUrl(b.url)).length;

  const categories = (issue.categories ?? []).filter(Boolean);
  // A section that has nothing in it. It used to send as an empty band, which
  // is why it blocked; the renderer now leaves it out entirely, so it is worth
  // saying it will be dropped and nothing more. The starter template creates
  // three of these, and a new newsletter opening with three red badges the
  // writer never earned is the wall this used to put in front of them.
  const unfinished = sections.filter((x) => {
    const has = (v?: string | null) => Boolean(v && v.trim());
    if (x.type === "image") return !has(x.image);
    if (x.type === "banner") return !has(x.image) && !has(x.heading);
    if (x.type === "cta") return !has(x.url);
    if (x.type === "tip" || x.type === "text" || x.type === "doctor") return !has(x.text);
    // A block needs at least one of its three visible parts.
    if (x.type === "block") return !has(x.heading) && !has(x.text) && !has(x.image);
    // A link section with nothing in it and no automatic filling sends nothing.
    if (x.type === "articles" || x.type === "featured") {
      return (x.articles?.length ?? 0) === 0 && (x.count ?? 0) === 0;
    }
    return false;
  });
  const hasContent = sections.length > 0 || blocks.length > 0;

  const checks: PreflightCheck[] = [
    {
      id: "audience",
      ok: categories.length > 0,
      tone: categories.length ? "ok" : "fail",
      label: categories.length ? "Audience chosen" : "No audience chosen",
      detail: categories.length
        ? `${categories.length} categor${categories.length === 1 ? "y" : "ies"}.`
        : undefined,
      fix: "Select at least one category — it decides who receives this and which articles they see.",
      anchor: "audience",
    },
    {
      id: "content",
      ok: hasContent,
      tone: !hasContent ? "fail" : unfinished.length ? "warn" : "ok",
      label: !hasContent ? "No content yet"
        : unfinished.length
          ? `${unfinished.length} empty section${unfinished.length === 1 ? "" : "s"} will be left out`
          : "Content ready",
      // Named the way the cards name themselves. This printed "featured" and
      // "banner" — labels that appear nowhere else in the interface.
      detail: unfinished.length
        ? unfinished.map((x) => x.heading?.trim() || SECTION_LABEL[x.type]).join(", ")
        : undefined,
      fix: !hasContent
        ? "Add a link, a photo or a banner — anything with content in it."
        : "Fill them in or remove them. Either way they will not be sent.",
      anchor: "content",
    },
    {
      id: "title",
      ok: issue.title.trim().length > 0,
      tone: issue.title.trim() ? "ok" : "fail",
      label: issue.title.trim() ? "Internal title set" : "No internal title",
      fix: "Give the newsletter an internal name so the team can find it.",
      anchor: "details",
    },
    {
      id: "subject",
      ok: issue.subject.trim().length > 0,
      tone: issue.subject.trim() ? "ok" : "fail",
      label: issue.subject.trim() ? "Subject line set" : "No subject line",
      fix: "Write the subject line recipients will see in their inbox.",
      anchor: "details",
    },
    {
      id: "preheader",
      ok: !!(issue.preheader && issue.preheader.trim()),
      tone: issue.preheader?.trim() ? "ok" : "warn",
      label: issue.preheader?.trim() ? "Preheader set" : "No preheader",
      fix: "Add the short line that follows the subject in the inbox.",
      anchor: "details",
      detail: issue.preheader?.trim() ? undefined : "Add a short line after the subject so the inbox is not empty.",
    },
    {
      id: "links",
      ok: broken.length === 0,
      tone: broken.length ? "fail" : "ok",
      label: broken.length ? "Some links will not work" : "Links are public https URLs",
      fix: "Every link must be a public https:// address.",
      anchor: "content",
      detail: broken.length ? `${broken.length} link(s) are missing or not https.` : undefined,
    },
    {
      id: "images",
      ok: missingImages.length === 0,
      tone: missingImages.length ? "warn" : "ok",
      label: missingImages.length ? "Some images will not appear" : "Images have https URLs",
      fix: "Give each image a public https:// address.",
      anchor: "content",
      detail: missingImages.length ? `${missingImages.length} image(s) will not appear in the sent email.` : undefined,
    },
    {
      id: "vars",
      ok: emptyVars.length === 0,
      tone: emptyVars.length ? "fail" : "ok",
      label: emptyVars.length ? "Unknown personalization token" : "Personalization tokens",
      fix: "Use {{first_name}} — other tokens send as literal text.",
      anchor: "content",
      detail: emptyVars.length ? `Unknown {{${emptyVars[0]}}}. Use {{first_name}} only.` : "{{first_name}} is supported.",
    },
    {
      id: "unsub",
      ok: true,
      tone: hasUnsub ? "ok" : "warn",
      label: hasUnsub ? "Unsubscribe present" : "No unsubscribe block",
      fix: "Add a Footer section so the unsubscribe line is yours rather than the default.",
      anchor: "content",
      detail: hasUnsub ? undefined : "Every send includes an unsubscribe link. Add a Footer block to customize it.",
    },
    {
      id: "utm",
      ok: utmGaps.length === 0,
      tone: utmGaps.length ? "fail" : "ok",
      label: utmGaps.length ? "Tracking incomplete" : "Tracking complete",
      fix: "Fill in the campaign name so this send reports separately.",
      anchor: "sending",
      detail: utmGaps.length ? `Missing ${utmGaps.join(", ")}.` : "utm_source, utm_medium, utm_campaign set.",
    },
    {
      id: "sender",
      ok: !!(issue.sender_name || "Altibbi").trim(),
      tone: "ok",
      label: "Sender configured",
      detail: `${issue.sender_name || "Altibbi"} · ${issue.reply_to || "newsletter@altibbi.me"}`,
    },
    {
      id: "spam",
      ok: !imageHeavy && linkCount <= 8,
      tone: imageHeavy || linkCount > 8 ? "warn" : "ok",
      label: imageHeavy || linkCount > 8 ? "May look like spam" : "Spam risk",
      fix: "Balance images with body text, and keep the number of links down.",
      anchor: "content",
      detail: imageHeavy
        ? "Mostly images and little body text."
        : linkCount > 8
          ? `${linkCount} links — consider fewer CTAs.`
          : "Looks like a normal health email.",
    },
  ];

  return {
    checks,
    blocking: checks.some((c) => !c.ok && c.tone === "fail"),
    warnings: checks.filter((c) => c.tone === "warn").length,
  };
}
